/**
 * Shopify Storefront API client (public token, browser-safe).
 * Uses plain fetch — no SDK dependency.
 * All product/cart data flows through here.
 */

const DOMAIN = process.env.REACT_APP_SHOPIFY_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION || "2025-01";
const ENDPOINT = `https://${DOMAIN}/api/${VERSION}/graphql.json`;

export const SHOPIFY_ENABLED = Boolean(DOMAIN && TOKEN);

const FALLBACK_IMAGE =
    "https://images.pexels.com/photos/163036/mario-luigi-yoschi-figures-163036.jpeg?auto=compress&cs=tinysrgb&w=900";

async function shopifyFetch(query, variables = {}) {
    if (!SHOPIFY_ENABLED) {
        throw new Error("Shopify credentials mancanti");
    }
    const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": TOKEN,
            Accept: "application/json",
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        console.error("Shopify errors", json.errors);
        throw new Error(json.errors[0]?.message || "Shopify error");
    }
    return json.data;
}

/* ---------- Queries ---------- */

const PRODUCT_FIELDS = /* GraphQL */ `
    id
    handle
    title
    description
    productType
    tags
    vendor
    availableForSale
    featuredImage {
        url
        altText
    }
    images(first: 20) {
        edges {
            node {
                url
                altText
            }
        }
    }
    priceRange {
        minVariantPrice {
            amount
            currencyCode
        }
    }
    variants(first: 5) {
        edges {
            node {
                id
                title
                availableForSale
                price {
                    amount
                    currencyCode
                }
            }
        }
    }
    metafields(
        identifiers: [
            { namespace: "custom", key: "bounty" }
            { namespace: "custom", key: "height_cm" }
            { namespace: "custom", key: "scale" }
            { namespace: "custom", key: "rarity" }
            { namespace: "custom", key: "crew" }
        ]
    ) {
        key
        value
    }
`;

const PRODUCTS_QUERY = /* GraphQL */ `
  query Products($first: Int!, $query: String) {
    products(first: $first, query: $query, sortKey: BEST_SELLING) {
      edges {
        node { ${PRODUCT_FIELDS} }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = /* GraphQL */ `
  query ProductByHandle($handle: String!) {
    productByHandle(handle: $handle) { ${PRODUCT_FIELDS} }
  }
`;

const SHOP_INFO_QUERY = /* GraphQL */ `
    query Shop {
        shop {
            name
            description
            primaryDomain {
                url
            }
        }
    }
`;

/* ---------- Mutations (Cart) ---------- */

const CART_FIELDS = /* GraphQL */ `
    id
    checkoutUrl
    totalQuantity
    cost {
        subtotalAmount {
            amount
            currencyCode
        }
        totalAmount {
            amount
            currencyCode
        }
    }
    lines(first: 100) {
        edges {
            node {
                id
                quantity
                merchandise {
                    ... on ProductVariant {
                        id
                        title
                        image {
                            url
                            altText
                        }
                        price {
                            amount
                            currencyCode
                        }
                        product {
                            id
                            handle
                            title
                            featuredImage {
                                url
                            }
                        }
                    }
                }
            }
        }
    }
`;

const CART_CREATE = /* GraphQL */ `
  mutation CartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

const CART_LINES_ADD = /* GraphQL */ `
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

const CART_LINES_UPDATE = /* GraphQL */ `
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

const CART_LINES_REMOVE = /* GraphQL */ `
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ${CART_FIELDS} }
      userErrors { field message }
    }
  }
`;

const CART_QUERY = /* GraphQL */ `
  query Cart($cartId: ID!) {
    cart(id: $cartId) { ${CART_FIELDS} }
  }
`;

/* ---------- Data normalizer ---------- */

function metaValue(metafields, key) {
    if (!Array.isArray(metafields)) return null;
    const m = metafields.find((x) => x && x.key === key);
    return m ? m.value : null;
}

const RARITY_TAG_PREFIX = "rarity:";
const CREW_TAG_PREFIX = "crew:";
const CATEGORY_TAG_PREFIX = "category:"; // capitano | grande-rotta | tesori | wanted

export function normalizeProduct(node) {
    if (!node) return null;
    const price = parseFloat(node.priceRange.minVariantPrice.amount);
    const currency = node.priceRange.minVariantPrice.currencyCode;
    const tags = node.tags || [];
    const variants = (node.variants?.edges || []).map((e) => e.node);
    const firstVariant = variants[0];
    const availableVariants = variants.filter((v) => v.availableForSale).length;

    const rarityFromTag = tags
        .find((t) => t.startsWith(RARITY_TAG_PREFIX))
        ?.slice(RARITY_TAG_PREFIX.length);
    const crewFromTag = tags
        .find((t) => t.startsWith(CREW_TAG_PREFIX))
        ?.slice(CREW_TAG_PREFIX.length);
    const categoryTag = tags
        .find((t) => t.startsWith(CATEGORY_TAG_PREFIX))
        ?.slice(CATEGORY_TAG_PREFIX.length);

    const rarity =
        metaValue(node.metafields, "rarity") ||
        rarityFromTag ||
        (tags.includes("pre-order") ? "Pre-ordine" : "Comune");

    const crew =
        metaValue(node.metafields, "crew") ||
        crewFromTag ||
        node.productType ||
        "Ciurma";

    return {
        id: node.id,
        // Numeric Shopify Product ID extracted from gid://shopify/Product/<id>
        externalId: (() => {
            const m = /\/Product\/(\d+)/.exec(node.id || "");
            return m ? m[1] : null;
        })(),
        handle: node.handle,
        name: node.title,
        character: node.title,
        crew,
        price,
        currency,
        image: node.featuredImage?.url || FALLBACK_IMAGE,
        images: (() => {
            const arr = (node.images?.edges || []).map((e) => ({
                url: e.node.url,
                alt: e.node.altText || node.title,
            }));
            // Ensure featured image appears first (Shopify usually already does this)
            if (arr.length === 0 && node.featuredImage?.url) {
                return [{ url: node.featuredImage.url, alt: node.title }];
            }
            return arr;
        })(),
        description: node.description || "",
        bounty:
            metaValue(node.metafields, "bounty") ||
            `${Math.floor(price * 5_000_000).toLocaleString("it-IT")} Berry`,
        height_cm:
            parseFloat(metaValue(node.metafields, "height_cm")) || 25,
        scale: metaValue(node.metafields, "scale") || "1/8",
        manufacturer: node.vendor || "Desa Shop",
        rarity,
        category: categoryTag, // one of: capitano/grande-rotta/tesori/wanted (from explicit tag)
        productType: node.productType || "",
        stock: node.availableForSale ? availableVariants || 10 : 0,
        featured: tags.includes("featured"),
        available: node.availableForSale,
        variantId: firstVariant?.id || null,
        tags,
    };
}

/* ---------- Public API ---------- */

export async function fetchProducts({ first = 30, filter = "" } = {}) {
    const data = await shopifyFetch(PRODUCTS_QUERY, {
        first,
        query: filter || null,
    });
    return data.products.edges.map((e) => normalizeProduct(e.node));
}

export async function fetchProductByHandle(handle) {
    const data = await shopifyFetch(PRODUCT_BY_HANDLE_QUERY, { handle });
    return normalizeProduct(data.productByHandle);
}

export async function fetchProductById(gid) {
    const query = /* GraphQL */ `
      query ProductById($id: ID!) {
        product(id: $id) { ${PRODUCT_FIELDS} }
      }
    `;
    const data = await shopifyFetch(query, { id: gid });
    return normalizeProduct(data.product);
}

export async function fetchProductRecommendations(productId, intent = "RELATED") {
    const query = /* GraphQL */ `
      query Recs($productId: ID!, $intent: ProductRecommendationIntent) {
        productRecommendations(productId: $productId, intent: $intent) { ${PRODUCT_FIELDS} }
      }
    `;
    try {
        const data = await shopifyFetch(query, { productId, intent });
        return (data.productRecommendations || []).map(normalizeProduct);
    } catch (e) {
        console.warn("Recommendations unavailable", e);
        return [];
    }
}

/**
 * Predictive live search (as-you-type suggestions).
 * Returns lightweight product summaries + query completions.
 */
export async function predictiveSearch(term, limit = 6) {
    if (!term || term.trim().length < 2) {
        return { products: [], queries: [] };
    }
    const query = /* GraphQL */ `
        query PredictiveSearch($q: String!, $limit: Int!) {
            predictiveSearch(
                query: $q
                types: [PRODUCT, QUERY]
                limit: $limit
            ) {
                products {
                    id
                    title
                    handle
                    productType
                    vendor
                    availableForSale
                    featuredImage {
                        url
                        altText
                    }
                    priceRange {
                        minVariantPrice {
                            amount
                            currencyCode
                        }
                    }
                }
                queries {
                    text
                }
            }
        }
    `;
    try {
        const data = await shopifyFetch(query, { q: term, limit });
        return {
            products: data.predictiveSearch.products || [],
            queries: (data.predictiveSearch.queries || []).map((q) => q.text),
        };
    } catch (e) {
        console.warn("Predictive search failed", e);
        return { products: [], queries: [] };
    }
}

export async function fetchShopInfo() {
    const data = await shopifyFetch(SHOP_INFO_QUERY);
    return data.shop;
}

/* ---------- Cart API ---------- */

export async function cartCreate(lines = []) {
    const data = await shopifyFetch(CART_CREATE, {
        input: { lines },
    });
    return data.cartCreate.cart;
}

export async function cartLinesAdd(cartId, lines) {
    const data = await shopifyFetch(CART_LINES_ADD, { cartId, lines });
    return data.cartLinesAdd.cart;
}

export async function cartLinesUpdate(cartId, lines) {
    const data = await shopifyFetch(CART_LINES_UPDATE, { cartId, lines });
    return data.cartLinesUpdate.cart;
}

export async function cartLinesRemove(cartId, lineIds) {
    const data = await shopifyFetch(CART_LINES_REMOVE, { cartId, lineIds });
    return data.cartLinesRemove.cart;
}

export async function fetchCart(cartId) {
    const data = await shopifyFetch(CART_QUERY, { cartId });
    return data.cart;
}

/* ---------- Customer Account ---------- */

const CUSTOMER_FIELDS = /* GraphQL */ `
    id
    firstName
    lastName
    email
    phone
    displayName
    createdAt
    numberOfOrders
    orders(first: 50, sortKey: PROCESSED_AT, reverse: true) {
        edges {
            node {
                id
                orderNumber
                name
                processedAt
                financialStatus
                fulfillmentStatus
                totalPrice {
                    amount
                    currencyCode
                }
                statusUrl
                lineItems(first: 20) {
                    edges {
                        node {
                            title
                            quantity
                            variant {
                                image {
                                    url
                                }
                                price {
                                    amount
                                    currencyCode
                                }
                                product {
                                    handle
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export async function customerLogin(email, password) {
    const query = /* GraphQL */ `
        mutation Login($input: CustomerAccessTokenCreateInput!) {
            customerAccessTokenCreate(input: $input) {
                customerAccessToken {
                    accessToken
                    expiresAt
                }
                customerUserErrors {
                    code
                    message
                }
            }
        }
    `;
    const data = await shopifyFetch(query, {
        input: { email, password },
    });
    const res = data.customerAccessTokenCreate;
    if (res.customerUserErrors?.length) {
        throw new Error(res.customerUserErrors[0].message);
    }
    return res.customerAccessToken;
}

export async function customerRegister({ email, password, firstName, lastName }) {
    const query = /* GraphQL */ `
        mutation Register($input: CustomerCreateInput!) {
            customerCreate(input: $input) {
                customer { id email }
                customerUserErrors { code field message }
            }
        }
    `;
    const data = await shopifyFetch(query, {
        input: { email, password, firstName, lastName, acceptsMarketing: true },
    });
    const res = data.customerCreate;
    if (res.customerUserErrors?.length) {
        throw new Error(res.customerUserErrors[0].message);
    }
    // Auto-login after register
    return customerLogin(email, password);
}

export async function customerLogout(accessToken) {
    const query = /* GraphQL */ `
        mutation Logout($token: String!) {
            customerAccessTokenDelete(customerAccessToken: $token) {
                deletedAccessToken
                userErrors { message }
            }
        }
    `;
    try {
        await shopifyFetch(query, { token: accessToken });
    } catch (_) {
        // ignore
    }
    return true;
}

export async function fetchCustomer(accessToken) {
    const query = /* GraphQL */ `
      query Customer($token: String!) {
        customer(customerAccessToken: $token) { ${CUSTOMER_FIELDS} }
      }
    `;
    const data = await shopifyFetch(query, { token: accessToken });
    return data.customer;
}

export function formatShopifyPrice(amount, currency = "EUR") {
    return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: currency || "EUR",
    }).format(parseFloat(amount));
}
